// Client-side PDF merge using pdf-lib.
// Constraint: max 6 files, each ≤ 85MB. Merge runs fully in browser memory.
// Architecture: see /ARCHITECTURE.md

import { PDFDocument } from "pdf-lib";

export interface MergeResult {
  blob: Blob;
  sizeBytes: number;
  pageCount: number;
}

export async function mergePdfFiles(files: File[]): Promise<MergeResult> {
  if (files.length === 0) throw new Error("No files to merge");
  if (files.length > 6) throw new Error("Max 6 files allowed");

  const outDoc = await PDFDocument.create();
  let pageCount = 0;

  for (const file of files) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    // load() throws on invalid PDF; catch to give a clearer error
    let src: PDFDocument;
    try {
      src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
      throw new Error(`Failed to read ${file.name}: ${(err as Error).message}`);
    }
    const indices = src.getPageIndices();
    const copied = await outDoc.copyPages(src, indices);
    for (const p of copied) outDoc.addPage(p);
    pageCount += indices.length;
  }

  const mergedBytes = await outDoc.save();
  // pdf-lib returns Uint8Array; copy into a fresh ArrayBuffer so the Blob
  // constructor accepts it across browsers.
  const buf = new ArrayBuffer(mergedBytes.byteLength);
  new Uint8Array(buf).set(mergedBytes);
  const blob = new Blob([buf], { type: "application/pdf" });

  return { blob, sizeBytes: blob.size, pageCount };
}
